/**
 * The Environments this device reaches over the user's own SSH, as the catalog sees them.
 *
 * Saved in Client Settings rather than with an account: a destination is only meaningful where
 * the user's SSH configuration and keys are, so an alias saved on one machine names nothing on
 * another. Nothing secret is written here — a password the person types answers one attempt.
 */
import type { PlatformConnectionChange } from "@afar-research/client-runtime/platform";
import { SshConnectionTarget } from "@afar-research/client-runtime/connection";
import type { EnvironmentId, SshConnectionSetting } from "@afar-research/contracts";

import { getClientSettingsSnapshot, persistClientSettingsUpdate } from "../clientSettingsStore";
import {
  isEnabledOnThisDevice,
  savedConnectionChanges,
  subscribeSavedSetting,
} from "./savedConnections";

export function readSshConnections(): ReadonlyArray<SshConnectionSetting> {
  return getClientSettingsSnapshot().sshConnections;
}

export function sshConnectionRegistration(connection: SshConnectionSetting): {
  readonly target: SshConnectionTarget;
  readonly enabled: boolean;
} {
  return {
    enabled: isEnabledOnThisDevice(connection.environmentId),
    target: new SshConnectionTarget({
      environmentId: connection.environmentId,
      label: connection.label,
      ssh: connection.target,
    }),
  };
}

/**
 * What the catalog should hold for the saved SSH Environments, given what it already holds.
 *
 * `canOpenForward` is false on a client with no way to open one — a browser, or a release, since
 * SSH Environments are a development-only route. Such a client publishes removals and no installs:
 * the destinations stay saved, and the catalog holds no row that cannot be connected to.
 */
export function sshEnvironmentConnectionChanges(input: {
  readonly saved: ReadonlyArray<SshConnectionSetting>;
  readonly published: ReadonlyArray<EnvironmentId>;
  readonly canOpenForward: boolean;
}): {
  readonly changes: ReadonlyArray<PlatformConnectionChange>;
  readonly published: ReadonlyArray<EnvironmentId>;
} {
  return savedConnectionChanges({
    registrations: (input.canOpenForward ? input.saved : []).map(sshConnectionRegistration),
    published: input.published,
  });
}

/**
 * Saves one SSH Environment, replacing any earlier entry for the same Environment.
 *
 * Keyed by the Environment rather than by the destination: the same machine reached through a new
 * alias is still that machine, and keeping both would put two rows in the catalog for one
 * Environment.
 */
export async function saveSshConnection(connection: SshConnectionSetting): Promise<void> {
  await persistClientSettingsUpdate((settings) => ({
    ...settings,
    sshConnections: [
      ...settings.sshConnections.filter(
        (entry) => entry.environmentId !== connection.environmentId,
      ),
      connection,
    ],
  }));
}

export async function forgetSshConnection(environmentId: EnvironmentId): Promise<void> {
  await persistClientSettingsUpdate((settings) => ({
    ...settings,
    sshConnections: settings.sshConnections.filter(
      (entry) => entry.environmentId !== environmentId,
    ),
  }));
}

/** Calls `listener` whenever the saved SSH set may have changed; see `subscribeSavedSetting`. */
export function subscribeSshConnections(listener: () => void): () => void {
  return subscribeSavedSetting(readSshConnections, listener);
}
